import { Link } from 'react-router-dom';
import { 
  Lightbulb, DollarSign, MapPin, Users, Mic2, Monitor, UtensilsCrossed, Palette, 
  Globe, Camera, UserCheck, Star, Gift, Shield, Truck, Music, FileCheck, 
  Megaphone, ClipboardCheck, BarChart3, ArrowRight, CheckCircle 
} from 'lucide-react';
import SEO from '../components/SEO';

const services = [
  {
    icon: Lightbulb,
    title: 'Event Planning & Concept Development',
    description: 'Creative ideation and strategic planning to bring your event vision to life with innovative concepts.',
  },
  {
    icon: DollarSign,
    title: 'Budget Planning & Management',
    description: 'Comprehensive financial planning and cost optimization to maximize your event ROI.',
  },
  {
    icon: MapPin,
    title: 'Venue Sourcing & Booking',
    description: 'Access to premium venues worldwide with negotiated rates and seamless booking.',
  },
  {
    icon: Users,
    title: 'Guest List & RSVP Management',
    description: 'Digital invitation management, RSVP tracking, and guest communication systems.',
  },
  {
    icon: Mic2,
    title: 'Vendor Coordination',
    description: 'End-to-end vendor management including sourcing, negotiation, and coordination.',
  },
  {
    icon: Monitor,
    title: 'Audio-Visual Setup',
    description: 'State-of-the-art AV equipment, LED walls, sound systems, and technical production.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Catering & Menu Planning',
    description: 'Curated culinary experiences from world-class caterers and celebrity chefs.',
  },
  {
    icon: Palette,
    title: 'Event Design & Decor',
    description: 'Bespoke event aesthetics, stage design, floral arrangements, and ambient lighting.',
  },
  {
    icon: Globe,
    title: 'Event Website or App Development',
    description: 'Custom digital platforms for registration, engagement, and virtual attendance.',
  },
  {
    icon: Camera,
    title: 'Photography & Videography',
    description: 'Professional coverage including live streaming, drone footage, and post-production.',
  },
  {
    icon: UserCheck,
    title: 'Hostess & Staffing',
    description: 'Trained event staff, hostesses, ushers, and customer service professionals.',
  },
  {
    icon: Star,
    title: 'VIP & Celebrity Management',
    description: 'Red carpet services, celebrity coordination, and exclusive VIP experiences.',
  },
  {
    icon: Gift,
    title: 'Gifting & Merchandise',
    description: 'Custom branded merchandise, gift bags, awards, and corporate souvenirs.',
  },
  {
    icon: Shield,
    title: 'Security & Crowd Management',
    description: 'Professional security services, access control, and emergency protocols.',
  },
  {
    icon: Truck,
    title: 'Transportation & Logistics',
    description: 'Fleet management, airport transfers, and seamless logistics coordination.',
  },
  {
    icon: Music,
    title: 'Entertainment & Speaker Management',
    description: 'Artist booking, performer coordination, and keynote speaker arrangements.',
  },
  {
    icon: FileCheck,
    title: 'Licensing & Permits',
    description: 'Event permits, legal compliance, insurance, and regulatory requirements.',
  },
  {
    icon: Megaphone,
    title: 'Digital Marketing & Promotions',
    description: 'Comprehensive marketing campaigns, PR, social media, and brand activation.',
  },
  {
    icon: ClipboardCheck,
    title: 'Onsite Event Execution & Coordination',
    description: 'Real-time management, problem-solving, and flawless event delivery.',
  },
  {
    icon: BarChart3,
    title: 'Post-Event Reporting & Feedback',
    description: 'Detailed analytics, attendee feedback, ROI analysis, and success metrics.',
  },
];

export const ServicesPage = () => {
  return (
    <main data-testid="services-page" className="pt-24 min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Our Services - Complete Event Management Solutions"
        description="Discover Golden Tree Events' full range of event management services including event planning, venue management, catering, entertainment, branding, logistics, and post-event analytics."
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">What We Offer</p>
            <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Our <span className="gradient-gold">Services</span>
            </h1>
            <p className="font-google-sans text-[#A1A1AA] text-lg max-w-3xl mx-auto">
              Comprehensive event management solutions from concept to execution. 
              We handle every detail so you can focus on what matters most.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-${index}`}
                className="group p-8 bg-white/[0.02] border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500"
              >
                <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:border-[#D4AF37] transition-colors">
                  <service.icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="font-montserrat text-white text-xl mb-3">{service.title}</h3>
                <p className="font-google-sans text-[#A1A1AA] text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Why Choose Us</p>
              <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-6">
                Excellence in Every <span className="gradient-gold">Detail</span>
              </h2>
              <div className="space-y-4">
                {[
                  'ISO 9001 Certified Quality Management',
                  '500+ Events Successfully Delivered',
                  'Global Network of Premium Vendors',
                  '24/7 Dedicated Event Support',
                  'Transparent Pricing & No Hidden Costs',
                  'Post-Event Analytics & Reporting',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-[#A1A1AA]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop"
                alt="Event"
                className="w-full aspect-video object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 border border-[#D4AF37]/30 hidden lg:block" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#D4AF37]">
        <div className="w-full px-4 sm:px-8 lg:px-16"> text-center">
          <h2 className="font-montserrat text-black text-3xl sm:text-4xl mb-4">
            Ready to Plan Your Event?
          </h2>
          <p className="font-google-sans text-black/70 mb-8">
            Let's discuss your requirements and create something extraordinary together.
          </p>
          <Link
            to="/contact-us"
            data-testid="services-contact-cta"
            className="inline-flex items-center gap-3 bg-black text-white font-medium px-10 py-4 uppercase tracking-widest text-sm hover:bg-black/80 transition-all duration-300"
          >
            Get a Free Quote
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
